import { Check, RefreshCw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUILD } from '@/config/build';
import { buildLabel } from '@/lib/build-label';
import { startedNewVersion, useQuietUpdate, useUpdate } from '@/lib/pwa';
import { Button, IconButton } from './primitives';

/** How long "Shycut was updated" stays up. */
const UPDATED_NOTICE_MS = 5000;

const TOAST =
  'pointer-events-auto flex animate-[rise-in_380ms_cubic-bezier(0.22,1,0.36,1)] items-center gap-3 rounded-2xl bg-panel py-2 pr-2 pl-4 shadow-float ring-1 ring-line';

/**
 * "A new version is ready" — shown only on the home page, where reloading
 * can't cost anything. While a video or a recording is open the new version
 * waits; with nothing open it also starts by itself once the app is left
 * (see `useQuietUpdate`). The first start of a new version says so, briefly.
 */
export function UpdateNotice() {
  const { t, i18n } = useTranslation();
  const apply = useUpdate();
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updated, setUpdated] = useState(startedNewVersion);
  useQuietUpdate();
  useEffect(() => {
    if (!updated) return;
    const timer = window.setTimeout(() => setUpdated(false), UPDATED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [updated]);

  if (updated && !apply) {
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div role="status" className={`${TOAST} pr-4`}>
          <span className="flex size-6 items-center justify-center rounded-full bg-accent text-accent-ink">
            <Check size={14} strokeWidth={3} />
          </span>
          <p className="text-sm text-fg">
            {t('update.updated')} <span className="text-fg-subtle tabular">· {buildLabel(BUILD, i18n.language)}</span>
          </p>
        </div>
      </div>
    );
  }
  if (!apply || dismissed) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div
        role="status"
        className={TOAST}
      >
        <p className="text-sm text-fg">{t('update.ready')}</p>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="primary"
            icon={<RefreshCw size={14} className={updating ? 'animate-spin' : undefined} />}
            disabled={updating}
            onClick={() => {
              setUpdating(true);
              void apply().catch(() => setUpdating(false));
            }}
          >
            {t('update.apply')}
          </Button>
          <IconButton label={t('update.later')} onClick={() => setDismissed(true)}>
            <X size={15} />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
